// Google Ads conversion labels for the Borg AI cost funnel. Each label belongs to
// a conversion action in Google Ads account 5319437792, created 2026-10-08, and is
// scoped to the BORG campaigns rather than the account-wide Search Atlas goals.
// Labels are public identifiers that ship in the page, exactly like the destination.
// The waitlist page offers two buttons, "join the waitlist" and "request an
// invitation". Both mean the same thing for bidding: this visitor did not
// qualify and left an email, so both report the one waitlist conversion.
export const WAITLIST_LABEL = 'fQDZCPWNpJYdELer7to9'
// Qualified lead is the only PRIMARY Borg action in Google Ads (2026-10-21): it
// fires once per person who passes costQualified(), booked or not. A booking is
// a qualified lead that also booked, so Meeting Booked stays secondary and the
// same person is never counted twice. Assessment complete fires for everyone,
// qualified or not, and is secondary.
export const CONVERSION_LABELS: Record<string, string> = {
  borg_qualified_lead: 'eUg1CLfHwKQdELer7to9',
  borg_assessment_complete: 'uVLCCPiNpJYdELer7to9',
  borg_book_click: 'LxsgCO-NpJYdELer7to9',
  borg_invite_request: WAITLIST_LABEL,
  borg_waitlist_join: WAITLIST_LABEL,
}

type Gtag = (...args: unknown[]) => void

// The funnel bridge reports outcomes by pushing a plain object onto dataLayer.
// gtag itself also pushes, but it pushes an Arguments object, so a string event
// name on an own property is what separates a funnel outcome from tag traffic.
export function funnelEventName(entry: unknown): string | undefined {
  if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) return undefined
  if (Object.prototype.toString.call(entry) === '[object Arguments]') return undefined
  const name = (entry as { event?: unknown }).event
  return typeof name === 'string' && name in CONVERSION_LABELS ? name : undefined
}

// Reports one Google Ads conversion per action per page load. Repeats are tracked
// by label, not by event name, so the two waitlist buttons cannot report the same
// conversion twice. The actions also count ONE_PER_CLICK, so Google discards
// repeats from the same click and no transaction id is needed. Values stay at the
// action default until close data exists, so no revenue figure is asserted here.
export function startConversionTracking(
  destinationId: string,
  dataLayer: unknown[],
  gtag: Gtag,
  reported: Set<string> = new Set(),
): void {
  const report = (entry: unknown) => {
    const name = funnelEventName(entry)
    if (!name) return
    const label = CONVERSION_LABELS[name]
    if (reported.has(label)) return
    reported.add(label)
    gtag('event', 'conversion', { send_to: `${destinationId}/${label}` })
  }
  // The bridge script is synchronous and this module is async, so outcomes can
  // already be queued before the tag exists. Drain first, then watch.
  for (const entry of [...dataLayer]) report(entry)
  const push = dataLayer.push.bind(dataLayer)
  // Defined rather than assigned: a plain assignment adds an enumerable own
  // property to the array, which changes its shape for anything that iterates
  // dataLayer, Google's own tag included.
  Object.defineProperty(dataLayer, 'push', {
    configurable: true,
    writable: true,
    enumerable: false,
    value: (...entries: unknown[]) => {
      const length = push(...entries)
      for (const entry of entries) report(entry)
      return length
    },
  })
}
